import { Badge } from '@components/Badge'
import { HeroIcon } from '@components/HeroIcon'
import { useMemo } from 'react'
import {
  Stack,
  XStack,
  Text,
  useMedia,
  useWindowDimensions,
  getTokens,
  isWeb,
} from 'tamagui'

interface IModuleSliderTitleProps {
  isSubmodule: boolean
  moduleName: string
  totalLessons: number
  numberOfLessons: number
  lessonsCompletedPerModule: number
}

export function ModuleSliderTitle({
  isSubmodule,
  moduleName,
  totalLessons,
  numberOfLessons,
  lessonsCompletedPerModule,
}: IModuleSliderTitleProps) {
  const media = useMedia()
  const { width } = useWindowDimensions()
  const sizes = getTokens().size

  const moduleTitleDisplay = useMemo(() => {
    return moduleName.length > 360 ? 'block' : 'flex'
  }, [moduleName.length])

  const moduleTitleFontSize = useMemo(() => {
    if (isWeb) return isSubmodule ? '$font-size-sm' : '$font-size-md'
    return isSubmodule ? '$font-size-xxs' : '$font-size-xs'
  }, [isSubmodule])

  const formattedModuleName = useMemo(() => {
    const spacingMd = Number(sizes['$spacing-md'].val)
    const nameMaxLength = (width - spacingMd) / (media.gtSm ? 4.7 : 3.5)
    return moduleName.length > nameMaxLength
      ? `${moduleName.slice(0, nameMaxLength)}...`
      : moduleName
  }, [media.gtSm, sizes, width, moduleName])

  const titleWidthByDevice = useMemo(() => {
    if (media.gtLg) return width * 0.8
    if (media.gtSm) return width * 0.4
    return width * 0.5
  }, [media.gtLg, media.gtSm, width])

  const moduleTitleWidth = useMemo(() => {
    return moduleName.length > 200 ? titleWidthByDevice : 'auto'
  }, [titleWidthByDevice, moduleName.length])

  return (
    <Stack
      flex={1}
      marginBottom="$size.spacing-xs"
      $gtSm={{ flexDirection: 'row', alignItems: 'center' }}
      gap="$spacing-inset-xs"
      $gtMd={{ width: '90%' }}
    >
      {isSubmodule && (
        <XStack
          borderRadius="$border-radius-lg"
          borderTopLeftRadius={0}
          borderEndEndRadius={0}
          bg="$bg-surface"
          alignItems="center"
          paddingHorizontal="$spacing-inset-xs"
          paddingVertical="$spacing-inset-nano"
          gap="$spacing-inset-xxs"
          alignSelf="flex-start"
        >
          <HeroIcon
            icon={['fal', 'turn-down-right']}
            color="$icon-secondary"
            size={20}
          />
          <Text
            color="$text-secondary"
            fontSize="$font-size-xs"
            ff="$mulishSemiBold"
          >
            Submódulo
          </Text>
        </XStack>
      )}

      <XStack
        flex={1}
        flexDirection="column"
        alignItems="flex-start"
        gap="$spacing-inset-nano"
        $gtSm={{
          display: moduleTitleDisplay,
          alignItems: 'center',
          flexDirection: 'row',
        }}
      >
        <Text
          color="$text-primary"
          ff="$mulishBold"
          fontSize={moduleTitleFontSize}
          mr="$spacing-inset-xs"
          width={moduleTitleWidth}
          fontWeight={700}
        >
          {formattedModuleName}
        </Text>
        {numberOfLessons !== 0 && (
          <XStack alignItems="center">
            <Text
              color="$progress-complete"
              ff="$mulishBold"
              fontSize="$font-size-xxs"
              whiteSpace="nowrap"
            >
              {lessonsCompletedPerModule}
            </Text>
            <Text
              display="inline-flex"
              color="$text-secondary"
              ff="$mulishBold"
              fontSize="$font-size-xxs"
              alignItems="center"
            >
              {`  de ${totalLessons} conteúdos`}
            </Text>
            {lessonsCompletedPerModule === numberOfLessons && (
              <Badge
                ml="$spacing-inset-xs"
                variant="success"
                text="Concluído"
                style={{
                  alignSelf: 'center',
                }}
              />
            )}
          </XStack>
        )}
      </XStack>
    </Stack>
  )
}
